import React from 'react';

// Public-good GeoJSON for the 50 US states + DC + PR. Stable URL — same one
// the mockup-proposals worktree used. We fetch once per app lifetime and
// cache in module scope so the InteractiveStatesMap and the
// address-derived-state lookup share a single download.
const US_STATES_URL =
  'https://raw.githubusercontent.com/PublicaMundi/MappingAPI/master/data/geojson/us-states.json';

let cachedData: GeoJSON.FeatureCollection | null = null;
let inFlight: Promise<GeoJSON.FeatureCollection> | null = null;

async function fetchOnce(): Promise<GeoJSON.FeatureCollection> {
  if (cachedData) return cachedData;
  if (inFlight) return inFlight;
  inFlight = fetch(US_STATES_URL).then(async (r) => {
    if (!r.ok) throw new Error(`US states fetch failed: ${r.status}`);
    const data = (await r.json()) as GeoJSON.FeatureCollection;
    cachedData = data;
    return data;
  });
  try {
    return await inFlight;
  } finally {
    inFlight = null;
  }
}

export interface UsStatesGeoResult {
  data: GeoJSON.FeatureCollection | null;
  loading: boolean;
  error: Error | null;
}

// Hook returning the GeoJSON. Multiple components calling this on the same
// page share the same fetch + cache.
export function useUSStatesGeo(): UsStatesGeoResult {
  const [data, setData] = React.useState<GeoJSON.FeatureCollection | null>(cachedData);
  const [loading, setLoading] = React.useState<boolean>(cachedData === null);
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    if (cachedData) return; // already loaded — no work
    let cancelled = false;
    setLoading(true);
    fetchOnce()
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setError(null);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { data, loading, error };
}

// Test escape hatch — vitest can pre-seed the cache (e.g., with a fixture)
// so components don't try to fetch. NEVER call from production code.
export function __setUSStatesGeoCacheForTests(data: GeoJSON.FeatureCollection | null): void {
  cachedData = data;
  inFlight = null;
}
